import {
  useOrganisation,
  usePermissions,
  useUpdateOrganisationTmsMenu,
} from '@my/directory-api/hooks';
import { Button, ErrorBanner, FormSection, Loading, Switch, showToast } from '@my/ui';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MODULES,
  parseMenuAreas,
  serialiseMenuAreas,
  type MenuAreaMap,
  type ModuleKey,
} from '@/auth/menuAreas';

export interface MenuVisibilitySectionProps {
  /** Owning organisation id; the card stays disabled while this is null. */
  organisationId: string | null;
}

/**
 * Which modules this organisation uses — the sidebar, and everything behind it.
 *
 * <p>
 * STRATUM SUPPORT ONLY, and it asks here as the sibling cards do. It has the
 * strongest claim to that gate of the three: the others hide UI, while this
 * changes the capability list the server computes, so an Admin with access
 * could quietly take Invoicing away from their whole company. The endpoint
 * enforces the same rule and answers 404 to a customer token — this is the
 * affordance, not the defence.
 * </p>
 *
 * <p>
 * Switching an area off removes it from the sidebar, from the route guards and
 * from the functional-role picker on Users & Access, from one stored fact
 * rather than three that could drift apart. That is also why the save
 * invalidates `me`: the screen the operator is standing on is drawn from the
 * list they just changed.
 * </p>
 *
 * <p>
 * Settings is deliberately not switchable. It is not in the vocabulary at all,
 * which is what makes this card impossible to lock yourself out of.
 * </p>
 */
export function MenuVisibilitySection({ organisationId }: MenuVisibilitySectionProps) {
  const { t } = useTranslation(['configuration', 'common']);
  const { role } = usePermissions();
  const organisationQuery = useOrganisation(organisationId);
  const saveMutation = useUpdateOrganisationTmsMenu();

  const stored = useMemo(
    () => parseMenuAreas(organisationQuery.data?.tmsMenuJson),
    [organisationQuery.data?.tmsMenuJson],
  );

  const [draft, setDraft] = useState<MenuAreaMap>(stored);

  // Re-seed whenever the server's copy changes — first load, and after a save
  // invalidates the query. Keyed on the serialised map so a refetch returning
  // identical data does not discard an edit in progress.
  const storedKey = serialiseMenuAreas(stored);
  useEffect(() => {
    setDraft(parseMenuAreas(storedKey));
  }, [storedKey]);

  const isDirty = serialiseMenuAreas(draft) !== storedKey;

  if (role !== 'SuperUser') return null;

  const toggle = (area: ModuleKey, next: boolean) =>
    setDraft((prev) => ({ ...prev, [area]: next }));

  const handleSave = async () => {
    if (!organisationId) return;
    try {
      await saveMutation.mutateAsync({
        organisationId,
        menuJson: serialiseMenuAreas(draft),
      });
      showToast({ type: 'success', message: t('configuration:menuVisibility.saved') });
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('configuration:menuVisibility.saveFailed'),
      });
    }
  };

  return (
    <FormSection
      title={t('configuration:menuVisibility.title')}
      columns={3}
      disabled={!organisationId}
      footer={
        <Button
          type="button"
          disabled={!isDirty || !organisationId}
          loading={saveMutation.isPending}
          onClick={handleSave}
        >
          {t('common:actions.save')}
        </Button>
      }
    >
      <p className="text-sm text-slate-500 md:col-span-3">
        {t('configuration:menuVisibility.description')}
      </p>

      {organisationQuery.isPending ? (
        <div className="md:col-span-3">
          <Loading label={t('common:states.loading')} />
        </div>
      ) : organisationQuery.isError ? (
        <div className="md:col-span-3">
          <ErrorBanner
            title={t('configuration:menuVisibility.loadFailed')}
            message={organisationQuery.error.message}
          />
        </div>
      ) : (
        MODULES.map((area) => (
          <Switch
            key={area}
            name={`menuArea-${area}`}
            label={t(`configuration:menuVisibility.areas.${area}`)}
            checked={draft[area]}
            enabledLabel={t('common:states.active')}
            disabledLabel={t('common:states.inactive')}
            onChange={(e) => toggle(area, e.target.checked)}
          />
        ))
      )}

      {/* Said plainly, because the reader is always support here: what they are
          looking at is not what the customer's own staff see. Their capability
          list skips this narrowing entirely, so the sidebar beside them stays
          full whatever is set on this card. */}
      <p className="text-xs text-slate-400 md:col-span-3">
        {t('configuration:menuVisibility.superUserNote')}
      </p>
    </FormSection>
  );
}
